import { resolveAssetUrl } from "@/utils/gameCatalog";
import { normalizeBackendRichText } from "@/utils/backendRichText";

const IMAGE_TYPE_PATTERN = /(IMAGE|IMG|PHOTO|PICTURE|BANNER|THUMBNAIL|이미지|사진|배너)/i;
const TEXT_TYPE_PATTERN = /(TEXT|HTML|BOARD|ARTICLE|CONTENT|글|텍스트|공지)/i;

export const stripHtml = (value) =>
  String(value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

export const extractFirstImageSrc = (html = "") => {
  if (!html || typeof document === "undefined" || !document.createElement) return "";

  const template = document.createElement("template");
  template.innerHTML = String(html);
  return template.content.querySelector("img")?.getAttribute("src") || "";
};

const uniqueUrls = (urls) => Array.from(new Set(urls.filter(Boolean)));

const getBoardFieldImages = (row = {}) =>
  uniqueUrls([
    resolveAssetUrl(row?.main_image),
    resolveAssetUrl(row?.pc_image),
    resolveAssetUrl(row?.mobile_image),
    resolveAssetUrl(row?.image),
    resolveAssetUrl(row?.image_url),
    resolveAssetUrl(row?.thumbnail),
    resolveAssetUrl(row?.thumbnail_url),
    resolveAssetUrl(row?.thumb_image),
    resolveAssetUrl(row?.banner),
    resolveAssetUrl(row?.banner_image),
    resolveAssetUrl(row?.file_url),
    resolveAssetUrl(row?.attachment_url),
    resolveAssetUrl(row?.attachments?.[0]?.url),
  ]);

const getBoardHtmlImages = (row = {}) =>
  uniqueUrls([
    resolveAssetUrl(extractFirstImageSrc(row?.title)),
    resolveAssetUrl(extractFirstImageSrc(row?.description || row?.content || row?.body || row?.message)),
  ]);

export const getBoardImageCandidates = (row = {}) =>
  uniqueUrls([
    ...getBoardFieldImages(row),
    ...getBoardHtmlImages(row),
  ]);

const getTypeSignals = (row = {}) =>
  [
    row?.display_type,
    row?.content_type,
    row?.template_type,
    row?.board_type,
    row?.notice_type,
    row?.event_type,
    row?.type,
    row?.category,
    row?.kind,
    row?.style_type,
  ]
    .filter((value) => value !== undefined && value !== null)
    .map(String);

export const getBoardDisplayKind = (row = {}, imageCandidates = [], options = {}) => {
  if (options.imageFormatEnabled === true) return imageCandidates.length ? "image" : "mixed";
  if (options.imageFormatEnabled === false) return "text";

  const signals = getTypeSignals(row);
  const joinedSignals = signals.join(" ");
  const imageFlag =
    row?.is_image === true ||
    row?.isImage === true ||
    row?.image_type === true ||
    row?.display_image === true;

  if (imageFlag || IMAGE_TYPE_PATTERN.test(joinedSignals)) return "image";
  if (TEXT_TYPE_PATTERN.test(joinedSignals)) return imageCandidates.length ? "mixed" : "text";
  if (imageCandidates.length) return "mixed";
  return "text";
};

export const getBoardContent = (row = {}, fallbackTitle = "게시글", options = {}) => {
  const rawBody = row?.description || row?.content || row?.body || row?.message || row?.detail || "";
  const html = normalizeBackendRichText(rawBody);
  const title = stripHtml(row?.title || row?.subject || row?.name || row?.message) || fallbackTitle;
  const fieldImages = getBoardFieldImages(row);
  const htmlImages = getBoardHtmlImages(row);
  const imageCandidates = uniqueUrls([...fieldImages, ...htmlImages]);
  const kind = getBoardDisplayKind(row, imageCandidates, options);
  const preview = stripHtml(rawBody || row?.summary || row?.sub_title || row?.subtitle || "");

  return {
    title,
    html,
    preview,
    images: imageCandidates,
    detailImages: fieldImages,
    kind,
  };
};
